import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  DictionaryAssetDeliveryStatus,
  DictionaryContractType,
} from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';
import { AssetAPIService } from 'src/app/extensions/services/api/asset-api.service';
import { FileStoreAPIService } from 'src/app/extensions/services/api/file-store-api.service';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';
import { BannerNotificationService } from 'src/app/extensions/services/banner-notification.service';
import { AssetInspectionModalComponent } from '../asset-modals/asset-inspection-modal/asset-inspection-modal.component';
import { AssetDeliveryDelegateModalComponent } from '../asset-modals/asset-delivery-delegate-modal/asset-delivery-delegate-modal.component';
import { MapModalComponent } from 'src/app/extensions/components/map-modal/map-modal.component';
import { ENV_CONFIG } from '../../../core/injection.token';
import { AuthorizeService } from '../../../core/services/authorize.service';
import { GlobalService } from '../../../extensions/services/global.service';

@Component({
  selector: 'asset-detail',
  standalone: false,
  templateUrl: './asset-detail.component.html',
  styleUrl: './asset-detail.component.scss',
})
export class AssetDetailComponent implements OnInit, OnDestroy {
  public asset: app.models.AssetDetailModel;
  public requestAccessWarnings: string[] = [];
  dataLoaded: boolean = false;
  path: string;
  accesToken: string;
  appId: string;
  returnUrl: string | null = null;

  AssetDeliveryStatus = DictionaryAssetDeliveryStatus;
  ContractType = DictionaryContractType;

  public constructor(
    private _assetAPIService: AssetAPIService,
    private _activatedRoute: ActivatedRoute,
    private _fileStoreAPIService: FileStoreAPIService,
    private _partnerAPIService: PartnerAPIService,
    private _router: Router,
    private _bannerService: BannerNotificationService,
    private _ngbModal: NgbModal,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvData,
    private _authorizeService: AuthorizeService,
    private _globalService: GlobalService
  ) {
    this.path = _envConfig.FILE_MANAGER_API_URL;
    this.accesToken = this._authorizeService.accessToken;
    this.appId = this._globalService.appId;
  }

  public ngOnInit(): void {
    this.returnUrl = this._activatedRoute.snapshot.queryParamMap.get('returnUrl');

    this.loadData();

    this._partnerAPIService.getPartnerRequestCreationRestrictions().subscribe(restrictions => {
      if (restrictions.isRestricted) {
        this.requestAccessWarnings.push('requests.cards.contactSupportWarning');
      }
      if (restrictions.hasUnpaidInvoices) {
        this.requestAccessWarnings.push('requests.cards.unpaidInvoicesWarning');
      }
    });
  }

  loadData() {
    const assetId = this._activatedRoute.snapshot.params['id'];
    this._assetAPIService.getAssetDetails(assetId).subscribe(data => {
      this.asset = data;

      if (this.asset.isAnyAssetInspectionTbd) {
        this.showMobileStoreBanner();
      }

      this.dataLoaded = true;
    });
  }

  showMobileStoreBanner() {
    this._bannerService.showBannerNotification({
      icon: 'icon-phone-02',
      translationString: 'asset.inspectionNotification',
      isVisible: true,
      appStoreLink: null,
      googlePlayLink: null,
    });
  }

  backHandler() {
    if (this.returnUrl) {
      this._router.navigate([decodeURIComponent(this.returnUrl)], {
        queryParams: { returnUrl: this.returnUrl.split('/')[1] },
      });
    } else {
      this._router.navigate(['asset-list']);
    }
  }

  ngOnDestroy(): void {
    this._bannerService.hideBannerNotification();
  }

  redirectDocumentRequest(contractId: string, assetId: string) {
    this._router.navigate(['requests/new', contractId, assetId]);
  }

  public navigateToContractHandler(contractId: string) {
    this._router.navigate(['contracts', contractId]);
  }

  downloadDocument(file: app.models.AssetDocumentModel) {
    if (file.documentStoragePath) {
      this._fileStoreAPIService.getFile(file.documentStoragePath).subscribe(response => {
        if (response) {
          const documentRef = `data:application/pdf;base64,${response}`;
          const downloadLink = document.createElement('a');

          downloadLink.href = documentRef;
          downloadLink.download = file.documentName;
          downloadLink.click();
        }
      });
    }
  }

  public downloadPhoto(photo: string): void {
    this._fileStoreAPIService.getFile(photo).subscribe(response => {
      if (response) {
        const documentRef = `data:application/png;base64,${response}`;
        const downloadLink = document.createElement('a');
        downloadLink.href = documentRef;
        downloadLink.download = photo;
        downloadLink.click();
      }
    });
  }

  openAssetInspectionModal() {
    this._ngbModal.open(AssetInspectionModalComponent, {
      centered: true,
      scrollable: true,
    });
  }

  openDelegateModal(asset: app.models.AssetDetailModel) {
    const modalRef = this._ngbModal.open(AssetDeliveryDelegateModalComponent, {
      scrollable: true,
    });
    modalRef.componentInstance.asset = asset;
  }

  openGoogleMaps(lat: number, lng: number) {
    if (!lat || !lng) {
      return;
    }

    const modalRef = this._ngbModal.open(MapModalComponent, {
      centered: true,
      scrollable: true,
      size: 'xl',
    });
    modalRef.componentInstance.lat = lat;
    modalRef.componentInstance.lng = lng;
  }
}
